/**
 * 4:3 中央クロップ + 車中心 smart crop (Wave 19)。
 *
 * - `calculateCropRect` は純粋関数 (Canvas 不要、Vitest で完全テスト可能)
 * - `cropTo4x3` は OffscreenCanvas で実際の画素を切り出す (ブラウザ / Worker 用)
 * - `calculateSmartCropRect` は車体 bbox を中心に、車が指定比率 (80-85%) を
 *   占める 4:3 クロップ矩形を計算する (Wave 19)
 * - `smartCropTo4x3` は Canvas で smart crop を実行する
 */

import type { CarBoundingBox } from '@shared/types';

import { CARSENSOR_SPEC, type CropRect } from './types';

/**
 * 元画像の幅・高さから 4:3 中央クロップの矩形を計算する。
 *
 * @param width - 元画像の幅 (px)
 * @param height - 元画像の高さ (px)
 * @param targetRatio - 目標アスペクト比 (デフォルト 4/3)
 * @returns ソース画像から切り出す矩形
 *
 * @example
 * calculateCropRect(1920, 1080) // 16:9 を左右カット → 1440×1080 の中央帯
 * calculateCropRect(768, 1024)  // 3:4 を上下カット → 768×576 の中央帯
 * calculateCropRect(1600, 1200) // 既に 4:3 → 全体
 */
export function calculateCropRect(
  width: number,
  height: number,
  targetRatio: number = CARSENSOR_SPEC.ASPECT_RATIO,
): CropRect {
  if (width <= 0 || height <= 0) {
    throw new RangeError(`Invalid dimensions: ${width}x${height}`);
  }
  if (targetRatio <= 0) {
    throw new RangeError(`Invalid targetRatio: ${targetRatio}`);
  }

  const currentRatio = width / height;

  if (currentRatio > targetRatio + CARSENSOR_SPEC.ASPECT_TOLERANCE) {
    // 横長すぎる → 左右をトリミング
    const newW = Math.round(height * targetRatio);
    return {
      sx: Math.floor((width - newW) / 2),
      sy: 0,
      sw: newW,
      sh: height,
    };
  }

  if (currentRatio < targetRatio - CARSENSOR_SPEC.ASPECT_TOLERANCE) {
    // 縦長すぎる → 上下をトリミング
    const newH = Math.round(width / targetRatio);
    return {
      sx: 0,
      sy: Math.floor((height - newH) / 2),
      sw: width,
      sh: newH,
    };
  }

  // 既に 4:3 (許容誤差内) → 全体を返す
  return { sx: 0, sy: 0, sw: width, sh: height };
}

/**
 * ImageBitmap を 4:3 中央クロップした OffscreenCanvas を返す。
 *
 * **注意:** Canvas API 依存。ブラウザ or Web Worker で実行すること。
 * Node.js (Vitest デフォルト jsdom) では動かない。実動作確認は
 * Playwright E2E (Wave 5) で行う。
 */
export async function cropTo4x3(source: ImageBitmap): Promise<ImageBitmap> {
  const rect = calculateCropRect(source.width, source.height);
  const canvas = new OffscreenCanvas(rect.sw, rect.sh);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('OffscreenCanvas 2D context unavailable');
  }
  ctx.drawImage(source, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, rect.sw, rect.sh);
  return canvas.transferToImageBitmap();
}

// ===== Wave 19: smart crop (車中心 4:3 クロップ) =========================

/**
 * 車を中心に、出力フレーム内で車が `targetFill` (デフォルト 0.82) の
 * 面積を占めるような 4:3 クロップ矩形を計算する純関数。
 *
 * アルゴリズム:
 *   1. bbox (0-1 正規化) をピクセル座標に変換
 *   2. 車が targetFill を占めるために必要な出力フレームサイズを計算
 *      (幅ベースと高さベースの両方で計算し、4:3 比率を満たす大きい方を採用)
 *   3. 画像全体を超えないようクランプ (4:3 比率は保持)
 *   4. 車中心を出力中心にして矩形を配置、画像端ではみ出さないよう clamp
 *
 * @param imgW 元画像の幅 (px)
 * @param imgH 元画像の高さ (px)
 * @param bbox 車体の bounding box (正規化 0-1)
 * @param targetFill 出力フレーム内で車が占めるべき面積比率 (0.7-0.9 推奨)
 * @param targetRatio 出力アスペクト比 (デフォルト 4:3)
 * @returns 元画像から切り出す 4:3 矩形 (ピクセル座標、整数に丸め)
 *
 * @throws RangeError - 引数が不正 (負値・範囲外等)
 *
 * @example
 * // 2560x1920 画像の中央に bbox x=0.3, y=0.3, w=0.4, h=0.4 の車 → ズームされた 4:3
 * calculateSmartCropRect(2560, 1920, { x: 0.3, y: 0.3, w: 0.4, h: 0.4 })
 * // → 車が 82% を占めるので出力フレームは bbox の 1/0.82 倍
 */
export function calculateSmartCropRect(
  imgW: number,
  imgH: number,
  bbox: CarBoundingBox,
  /**
   * 出力フレーム内で「拡張後 bbox」が占めるべき比率 (0-1)。
   * Wave 21.12: **1.0** (拡張 bbox ピッタリ)。実車は拡張 bbox の中央 90% に
   *   収まる想定なので、実質 90% 占有で十分大きい。
   */
  targetFill: number = 1.0,
  targetRatio: number = CARSENSOR_SPEC.ASPECT_RATIO,
  /**
   * bbox 補正率。
   * - 正値: 内側にシュリンク (Wave 21.9 以前で使用、車切れリスクあり)
   * - ゼロ: 補正なし (Wave 21.10-21.11、Claude bbox ピッタリ採用)
   * - 負値: 外側に拡張 (Wave 21.12)、Claude bbox が車の突起物を見逃したケース対策
   *
   * Wave 21.12: -0.10 (10% 拡張、安全マージン 5%) で切れゼロ達成。
   * Wave 21.13: 田中さん「もう少し大きく」要望 → **-0.08 に微緩和** (8% 拡張、
   *   安全マージン 4%)。80 枚中 1-2 枚で切れる可能性残るが、サイズ感優先で
   *   段階的に試す方針 (B プラン)。
   */
  bboxShrinkRatio: number = -0.08,
): CropRect {
  validateSmartCropInputs(imgW, imgH, bbox, targetFill, targetRatio);

  // bbox を拡張/縮小 (中心は維持、サイズだけ調整)
  const shrunk = shrinkBbox(bbox, bboxShrinkRatio);

  // bbox をピクセル座標に変換
  const carW = shrunk.w * imgW;
  const carH = shrunk.h * imgH;

  // 車が targetFill の比率を占めるように、幅/高さの両方基準でフレームサイズ計算
  const neededByW = carW / targetFill;
  const neededByH = carH / targetFill;

  // 4:3 比率を満たす最小フレーム (車が必ず収まる方を採用)
  let frameW: number;
  let frameH: number;
  if (neededByW >= neededByH * targetRatio) {
    frameW = neededByW;
    frameH = frameW / targetRatio;
  } else {
    frameH = neededByH;
    frameW = frameH * targetRatio;
  }

  const adjusted = adjustFrameForImageCenter(shrunk, frameW, frameH, imgW, imgH, targetRatio);
  frameW = adjusted.frameW;
  frameH = adjusted.frameH;

  // 画像中心に配置 (絶対維持)
  const fcx = imgW / 2;
  const fcy = imgH / 2;
  const sx = Math.max(0, Math.min(fcx - frameW / 2, imgW - frameW));
  const sy = Math.max(0, Math.min(fcy - frameH / 2, imgH - frameH));

  return {
    sx: Math.round(sx),
    sy: Math.round(sy),
    sw: Math.round(frameW),
    sh: Math.round(frameH),
  };
}

function validateSmartCropInputs(
  imgW: number,
  imgH: number,
  bbox: CarBoundingBox,
  targetFill: number,
  targetRatio: number,
): void {
  if (imgW <= 0 || imgH <= 0) {
    throw new RangeError(`Invalid dimensions: ${imgW}x${imgH}`);
  }
  if (targetRatio <= 0) {
    throw new RangeError(`Invalid targetRatio: ${targetRatio}`);
  }
  if (targetFill <= 0 || targetFill > 1) {
    throw new RangeError(`targetFill must be (0, 1], got ${targetFill}`);
  }
  validateBbox(bbox);
}

function validateBbox(bbox: CarBoundingBox): void {
  if (bbox.x < 0 || bbox.y < 0 || bbox.w <= 0 || bbox.h <= 0) {
    throw new RangeError(`Invalid bbox: ${JSON.stringify(bbox)}`);
  }
  if (bbox.x + bbox.w > 1 + 1e-6 || bbox.y + bbox.h > 1 + 1e-6) {
    throw new RangeError(`bbox exceeds image bounds: ${JSON.stringify(bbox)}`);
  }
}

/**
 * Wave 21.15: 画像中心置き frame サイズを調整。
 * bbox を収めるため必要なら frame を大きくし、4:3 比率を保ち、
 * 画像全体を超える場合は縮小する。
 */
function adjustFrameForImageCenter(
  bbox: CarBoundingBox,
  frameW: number,
  frameH: number,
  imgW: number,
  imgH: number,
  targetRatio: number,
): { frameW: number; frameH: number } {
  const minFrame = minFrameToContainBboxAtImageCenter(bbox, imgW, imgH);
  let w = Math.max(frameW, minFrame.frameW);
  let h = Math.max(frameH, minFrame.frameH);
  if (w / h > targetRatio) {
    h = w / targetRatio;
  } else {
    w = h * targetRatio;
  }
  if (w > imgW || h > imgH) {
    const scale = Math.min(imgW / w, imgH / h);
    w *= scale;
    h *= scale;
  }
  return { frameW: w, frameH: h };
}

/**
 * Wave 21.15: bbox を画像中心置き frame に収めるために必要な最小 frame サイズ。
 *
 * bbox 中心が画像中心からズレている場合、画像中心置きで bbox 全体を frame に
 * 含めるには frame を大きくする必要がある。この関数は必要な最小 frame 半分幅
 * (= 画像中心から bbox の遠い端までの距離) を計算して返す。
 *
 * 呼び出し側はこの最小値と、targetFill/expand で計算した理想 frame サイズを
 * 比較し、大きい方を採用することで bbox が必ず frame 内に収まる + 画像中心
 * 配置が両立する。
 */
function minFrameToContainBboxAtImageCenter(
  bbox: CarBoundingBox,
  imgW: number,
  imgH: number,
): { frameW: number; frameH: number } {
  const imgCx = imgW / 2;
  const imgCy = imgH / 2;
  const bboxLeft = bbox.x * imgW;
  const bboxRight = (bbox.x + bbox.w) * imgW;
  const bboxTop = bbox.y * imgH;
  const bboxBottom = (bbox.y + bbox.h) * imgH;
  // 画像中心から bbox のより遠い端までの距離 × 2
  const halfW = Math.max(imgCx - bboxLeft, bboxRight - imgCx);
  const halfH = Math.max(imgCy - bboxTop, bboxBottom - imgCy);
  return { frameW: halfW * 2, frameH: halfH * 2 };
}

/**
 * Wave 21.3: bbox を中心はそのままサイズ調整。
 * Wave 21.12: 負値対応 (外側に拡張、画像端でクランプ)。
 *
 * - ratio > 0: 内側にシュリンク (4 辺から ratio/2 ずつ削る)
 * - ratio = 0: そのまま
 * - ratio < 0: 外側に拡張 (4 辺を |ratio|/2 ずつ外に伸ばす、画像端で clamp)
 */
function shrinkBbox(bbox: CarBoundingBox, ratio: number): CarBoundingBox {
  if (ratio === 0) return bbox;
  if (ratio > 0) {
    const clampedRatio = Math.min(0.9, ratio);
    const margin = clampedRatio / 2;
    return {
      x: bbox.x + bbox.w * margin,
      y: bbox.y + bbox.h * margin,
      w: bbox.w * (1 - clampedRatio),
      h: bbox.h * (1 - clampedRatio),
    };
  }
  // ratio < 0: expand (中心維持型)
  //
  // Wave 21.13 のバグ修正 (田中さん指摘):
  // 旧実装は「左右を独立に伸ばして画像端で clamp」していた。結果、bbox が
  // 画像端に近い写真では右端だけ clamp され、bbox 中心が**非対称にシフト**。
  // smart crop は bbox 中心を出力中心にするので、出力内で車が「寄ったまま」
  // 見える現象が発生 (田中さんの XM 写真で観察)。
  //
  // 正しい実装: 中心を固定したまま左右/上下を均等に伸ばす。画像端に達する
  // 場合、expand 効果は制限されるが中心シフトは絶対に起きない。
  const expandAbs = Math.min(2.0, -ratio);
  const cx = bbox.x + bbox.w / 2;
  const cy = bbox.y + bbox.h / 2;
  const wantedHalfW = (bbox.w * (1 + expandAbs)) / 2;
  const wantedHalfH = (bbox.h * (1 + expandAbs)) / 2;
  // 中心から画像端までの距離で抑える (中心対称を保証)
  const actualHalfW = Math.min(wantedHalfW, cx, 1 - cx);
  const actualHalfH = Math.min(wantedHalfH, cy, 1 - cy);
  return {
    x: cx - actualHalfW,
    y: cy - actualHalfH,
    w: actualHalfW * 2,
    h: actualHalfH * 2,
  };
}

/**
 * ImageBitmap を bbox 中心に smart crop した OffscreenCanvas を返す。
 * ブラウザ / Web Worker 専用 (OffscreenCanvas 依存)。
 */
export async function smartCropTo4x3(
  source: ImageBitmap,
  bbox: CarBoundingBox,
  targetFill?: number,
): Promise<ImageBitmap> {
  const rect = calculateSmartCropRect(source.width, source.height, bbox, targetFill ?? 0.95);
  const canvas = new OffscreenCanvas(rect.sw, rect.sh);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('OffscreenCanvas 2D context unavailable');
  }
  ctx.drawImage(source, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, rect.sw, rect.sh);
  return canvas.transferToImageBitmap();
}
